/**
 * "Add to Home Screen" prompt.
 *
 * Chrome fires beforeinstallprompt and lets us defer it to a moment of our
 * choosing. iOS has no such event, so Safari users get instructions
 * instead -- shown only once, and only after they have actually used the
 * app rather than on first load.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Snackbar, Typography,
} from '@mui/material'
import IosShareIcon from '@mui/icons-material/IosShare'
import { useTranslation } from 'react-i18next'

const KEY = 'bv.installDismissed'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    // iOS reports installed state on navigator, not via matchMedia.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

export default function InstallPrompt() {
  const { t } = useTranslation()
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIos, setShowIos] = useState(false)

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(KEY)) return

    const onPrompt = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', onPrompt)

    // Safari never fires the event, so offer instructions after a delay --
    // long enough that the prompt does not greet a first-time visitor.
    let timer: number | undefined
    if (isIos()) timer = window.setTimeout(() => setShowIos(true), 45_000)

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      if (timer) window.clearTimeout(timer)
    }
  }, [])

  function dismiss() {
    localStorage.setItem(KEY, '1')
    setDeferred(null)
    setShowIos(false)
  }

  async function install() {
    if (!deferred) return
    await deferred.prompt()
    await deferred.userChoice
    dismiss()
  }

  const open = !!deferred || showIos

  return (
    <Snackbar open={open} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ mb: { xs: 8, md: 2 } }}>
      <Alert severity="info" onClose={dismiss}
        action={deferred ? (
          <Button size="small" onClick={() => void install()}>
            {t('pwa.install')}
          </Button>
        ) : undefined}>
        {deferred ? t('pwa.installHint', { app: t('app.name') }) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">{t('pwa.iosHint1')}</Typography>
            <IosShareIcon sx={{ fontSize: 17 }} />
            <Typography variant="body2">{t('pwa.iosHint2')}</Typography>
          </Box>
        )}
      </Alert>
    </Snackbar>
  )
}
